import React, { useState } from 'react';
import {
  ArrowLeft,
  Clock,
  DollarSign,
  TrendingUp,
  Info,
  Globe,
  Briefcase,
  Award,
  Bitcoin,
  Cpu,
  BarChart3,
  Gamepad2,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Typography } from '../styles/primitives';
import { FormattedMarket } from '../services/polymarketApi';
import { formatVolume } from './EventMarketCard';
import EventPriceChart from './EventPriceChart';
import EventOrderBook from './EventOrderBook';

interface EventDetailViewProps {
  market: FormattedMarket;
  relatedMarkets: FormattedMarket[];
  onBack: () => void;
  onRelatedClick: (market: FormattedMarket) => void;
  usdtBalance: number;
  onBet: (outcomeIndex: number, amount: number, yesOrNo: 'yes' | 'no') => Promise<void>;
  isSubmitting: boolean;
}

const categoryIcons: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  politics: Briefcase,
  sports: Award,
  crypto: Bitcoin,
  economy: BarChart3,
  tech: Cpu,
  general: Gamepad2,
};

function getTimeRemaining(endDate?: string) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days > 365) return `${Math.floor(days / 365)}y ${days % 365}d`;
  if (days > 30) return `${Math.floor(days / 30)}mo ${days % 30}d`;
  if (days > 0) {
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `${days}d ${hours}h`;
  }
  const hours = Math.floor(diff / (1000 * 60 * 60));
  return `${hours}h`;
}

const EventDetailView: React.FC<EventDetailViewProps> = ({
  market,
  relatedMarkets,
  onBack,
  onRelatedClick,
  usdtBalance,
  onBet,
  isSubmitting,
}) => {
  const [selectedOutcome, setSelectedOutcome] = useState<number>(0);
  const [yesOrNo, setYesOrNo] = useState<'yes' | 'no'>('yes');
  const [amount, setAmount] = useState('');
  const [showFullDescription, setShowFullDescription] = useState(false);

  const CategoryIcon = categoryIcons[market.category] || Globe;
  const amountNum = parseFloat(amount) || 0;
  const selectedOutcomeData = market.outcomes[selectedOutcome];

  const yesPrice = selectedOutcomeData?.price || 0;
  const noPrice = 1 - yesPrice;
  const activePrice = yesOrNo === 'yes' ? yesPrice : noPrice;
  const potentialPayout = activePrice > 0 ? amountNum / activePrice : 0;
  const timeRemaining = getTimeRemaining(market.endDate);

  const handleQuickAmount = (val: number) => {
    setAmount((prev) => {
      const current = parseFloat(prev) || 0;
      return String(current + val);
    });
  };

  const handleTrade = async () => {
    if (amountNum <= 0 || amountNum > usdtBalance) return;
    await onBet(selectedOutcome, amountNum, yesOrNo);
    setAmount('');
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-6 group/back transition-colors"
        style={{ color: '#64748b', fontSize: Typography.BodySmall }}
      >
        <ArrowLeft size={18} className="transition-transform group-hover/back:-translate-x-1" />
        <span>Back to Markets</span>
      </button>

      <div className="flex items-start gap-4 mb-6">
        {market.image ? (
          <img
            src={market.image}
            alt=""
            style={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              objectFit: 'cover',
              flexShrink: 0,
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'rgba(217, 18, 34, 0.08)',
            }}
          >
            <CategoryIcon size={28} style={{ color: '#d91222' }} />
          </div>
        )}
        <div>
          <span
            style={{
              fontSize: Typography.Caption,
              color: '#d91222',
              fontWeight: Typography.WeightSemibold,
              textTransform: 'capitalize',
            }}
          >
            {market.category}
          </span>
          <h1
            style={{
              fontSize: Typography.HeadlineLarge,
              fontWeight: Typography.WeightBold,
              color: '#0f172a',
              lineHeight: Typography.LeadingTight,
              marginTop: '4px',
            }}
          >
            {market.question}
          </h1>
        </div>
      </div>

      <div
        className="flex items-center gap-4 flex-wrap mb-8 pb-6"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}
      >
        <div className="flex items-center gap-1.5">
          <DollarSign size={14} style={{ color: '#64748b' }} />
          <span
            className="font-mono"
            style={{
              fontSize: Typography.BodySmall,
              color: '#475569',
              fontWeight: Typography.WeightSemibold,
            }}
          >
            {formatVolume(market.volume)} Vol.
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <TrendingUp size={14} style={{ color: '#64748b' }} />
          <span
            className="font-mono"
            style={{ fontSize: Typography.BodySmall, color: '#475569' }}
          >
            {formatVolume(market.liquidity)} Liquidity
          </span>
        </div>
        {timeRemaining && (
          <div className="flex items-center gap-1.5">
            <Clock size={14} style={{ color: '#64748b' }} />
            <span style={{ fontSize: Typography.BodySmall, color: '#475569' }}>
              {timeRemaining} remaining
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          {selectedOutcomeData?.tokenId && (
            <div className="space-y-4 mb-6">
              <EventPriceChart
                tokenId={selectedOutcomeData.tokenId}
                outcomeName={selectedOutcomeData.name}
              />
              <EventOrderBook tokenId={selectedOutcomeData.tokenId} />
            </div>
          )}

          <div className="space-y-3 mb-8">
            {market.outcomes.map((outcome, index) => {
              const isSelected = selectedOutcome === index;
              const yesCents = (outcome.price * 100).toFixed(1);
              const noCents = ((1 - outcome.price) * 100).toFixed(1);

              return (
                <div
                  key={outcome.name}
                  onClick={() => setSelectedOutcome(index)}
                  className="cursor-pointer transition-all duration-150"
                  style={{
                    background: isSelected ? 'rgba(217, 18, 34, 0.03)' : '#ffffff',
                    border: isSelected
                      ? '2px solid rgba(217, 18, 34, 0.25)'
                      : '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '14px',
                    padding: isSelected ? '15px' : '16px',
                  }}
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {isSelected && (
                        <CheckCircle2 size={16} style={{ color: '#d91222', flexShrink: 0 }} />
                      )}
                      <span
                        style={{
                          fontSize: Typography.BodyLarge,
                          fontWeight: Typography.WeightSemibold,
                          color: '#0f172a',
                        }}
                      >
                        {outcome.name}
                      </span>
                    </div>

                    <span
                      className="font-mono flex-shrink-0"
                      style={{
                        fontSize: Typography.HeadlineMedium,
                        fontWeight: Typography.WeightBold,
                        color: '#0f172a',
                      }}
                    >
                      {outcome.probability.toFixed(0)}%
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="font-mono"
                      style={{ fontSize: Typography.Caption, color: '#64748b' }}
                    >
                      Yes {yesCents}c
                    </span>
                    <span style={{ color: '#d4d4d4' }}>|</span>
                    <span
                      className="font-mono"
                      style={{ fontSize: Typography.Caption, color: '#64748b' }}
                    >
                      No {noCents}c
                    </span>
                  </div>

                  <div
                    style={{
                      height: 4,
                      background: 'rgba(0,0,0,0.05)',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(outcome.probability, 100)}%`,
                        height: '100%',
                        background: isSelected
                          ? 'linear-gradient(90deg, #d91222, #ef4444)'
                          : 'linear-gradient(90deg, #94a3b8, #cbd5e1)',
                        borderRadius: '2px',
                        transition: 'all 300ms ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {market.description && (
            <div
              className="mb-8 p-5"
              style={{
                background: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '14px',
              }}
            >
              <h3
                className="mb-3 flex items-center gap-2"
                style={{
                  fontSize: Typography.BodyLarge,
                  fontWeight: Typography.WeightSemibold,
                  color: '#0f172a',
                }}
              >
                <Info size={16} />
                Rules
              </h3>
              <div
                style={{
                  fontSize: Typography.BodySmall,
                  color: '#475569',
                  lineHeight: Typography.LeadingRelaxed,
                  maxHeight: showFullDescription ? 'none' : '120px',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {market.description}
                {!showFullDescription && market.description.length > 200 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '48px',
                      background: 'linear-gradient(transparent, #ffffff)',
                    }}
                  />
                )}
              </div>
              {market.description.length > 200 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  style={{
                    fontSize: Typography.Caption,
                    color: '#d91222',
                    fontWeight: Typography.WeightSemibold,
                    marginTop: '8px',
                  }}
                >
                  {showFullDescription ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

        </div>

        <div className="lg:w-[340px] flex-shrink-0">
          <div
            className="lg:sticky lg:top-4"
            style={{
              background: '#ffffff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <div className="p-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-center gap-3">
                {market.image && (
                  <img
                    src={market.image}
                    alt=""
                    style={{ width: 28, height: 28, borderRadius: '7px', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightSemibold,
                    color: '#0f172a',
                  }}
                >
                  {selectedOutcomeData?.name || 'Select outcome'}
                </span>
              </div>
            </div>

            <div className="p-4 pb-0">
              <div className="flex gap-2">
                <button
                  onClick={() => setYesOrNo('yes')}
                  className="flex-1 py-3 text-center transition-all duration-150"
                  style={{
                    background: yesOrNo === 'yes' ? '#10b981' : 'rgba(16, 185, 129, 0.08)',
                    color: yesOrNo === 'yes' ? '#ffffff' : '#10b981',
                    fontSize: Typography.BodySmall,
                    fontWeight: Typography.WeightBold,
                    borderRadius: '10px',
                    border: yesOrNo === 'yes' ? 'none' : '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  Yes {(yesPrice * 100).toFixed(1)}c
                </button>
                <button
                  onClick={() => setYesOrNo('no')}
                  className="flex-1 py-3 text-center transition-all duration-150"
                  style={{
                    background: yesOrNo === 'no' ? '#ef4444' : 'rgba(239, 68, 68, 0.06)',
                    color: yesOrNo === 'no' ? '#ffffff' : '#ef4444',
                    fontSize: Typography.BodySmall,
                    fontWeight: Typography.WeightBold,
                    borderRadius: '10px',
                    border: yesOrNo === 'no' ? 'none' : '1px solid rgba(239, 68, 68, 0.15)',
                  }}
                >
                  No {(noPrice * 100).toFixed(1)}c
                </button>
              </div>
            </div>

            <div className="p-4">
              <label
                style={{
                  fontSize: Typography.Caption,
                  color: '#64748b',
                  fontWeight: Typography.WeightSemibold,
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                Amount
              </label>
              <div className="relative">
                <span
                  className="absolute left-3 top-1/2 -translate-y-1/2 font-mono"
                  style={{ color: '#94a3b8', fontSize: Typography.BodyLarge }}
                >
                  $
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="w-full font-mono pl-8 pr-4 py-3 focus:outline-none"
                  style={{
                    background: 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '10px',
                    fontSize: Typography.HeadlineMedium,
                    fontWeight: Typography.WeightBold,
                    color: '#0f172a',
                  }}
                />
              </div>

              <div className="flex gap-2 mt-3">
                {[1, 5, 10, 100].map((val) => (
                  <button
                    key={val}
                    onClick={() => handleQuickAmount(val)}
                    className="flex-1 py-2 text-center transition-colors duration-100"
                    style={{
                      background: 'rgba(0,0,0,0.03)',
                      color: '#475569',
                      fontSize: Typography.Caption,
                      fontWeight: Typography.WeightSemibold,
                      borderRadius: '8px',
                      border: '1px solid rgba(0,0,0,0.07)',
                    }}
                  >
                    +${val}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setAmount(String(Math.floor(usdtBalance * 100) / 100))
                  }
                  className="flex-1 py-2 text-center transition-colors duration-100"
                  style={{
                    background: 'rgba(0,0,0,0.03)',
                    color: '#475569',
                    fontSize: Typography.Caption,
                    fontWeight: Typography.WeightSemibold,
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.07)',
                  }}
                >
                  Max
                </button>
              </div>
            </div>

            {amountNum > 0 && (
              <div className="px-4 pb-4">
                <div
                  className="p-3"
                  style={{
                    background: yesOrNo === 'yes'
                      ? 'rgba(16, 185, 129, 0.06)'
                      : 'rgba(239, 68, 68, 0.04)',
                    borderRadius: '10px',
                    border: yesOrNo === 'yes'
                      ? '1px solid rgba(16, 185, 129, 0.12)'
                      : '1px solid rgba(239, 68, 68, 0.1)',
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontSize: Typography.Caption, color: '#64748b' }}>
                      To win
                    </span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: Typography.HeadlineSmall,
                        fontWeight: Typography.WeightBold,
                        color: yesOrNo === 'yes' ? '#059669' : '#dc2626',
                      }}
                    >
                      ${potentialPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>
                      Avg. Price {(activePrice * 100).toFixed(1)}c
                    </span>
                    <span
                      className="font-mono"
                      style={{ fontSize: Typography.Fine, color: '#94a3b8' }}
                    >
                      {activePrice > 0 ? (amountNum / activePrice).toFixed(2) : '0'} shares
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="px-4 pb-4">
              <button
                onClick={handleTrade}
                disabled={isSubmitting || amountNum <= 0 || amountNum > usdtBalance}
                className="w-full py-3.5 flex items-center justify-center gap-2 transition-all duration-150"
                style={{
                  background:
                    amountNum > 0 && amountNum <= usdtBalance
                      ? yesOrNo === 'yes'
                        ? '#10b981'
                        : '#ef4444'
                      : 'rgba(0,0,0,0.06)',
                  color:
                    amountNum > 0 && amountNum <= usdtBalance ? '#ffffff' : '#94a3b8',
                  fontSize: Typography.BodyMedium,
                  fontWeight: Typography.WeightBold,
                  borderRadius: '10px',
                  cursor:
                    amountNum > 0 && amountNum <= usdtBalance
                      ? 'pointer'
                      : 'not-allowed',
                }}
              >
                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : 'Trade'}
              </button>
              <div className="text-center mt-3">
                <span
                  className="font-mono"
                  style={{ fontSize: Typography.Fine, color: '#94a3b8' }}
                >
                  Available: ${usdtBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {relatedMarkets.length > 0 && (
              <div
                className="px-4 pb-4 pt-4"
                style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
              >
                <h4
                  className="mb-3"
                  style={{
                    fontSize: Typography.Caption,
                    fontWeight: Typography.WeightSemibold,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Related Markets
                </h4>
                <div className="space-y-3">
                  {relatedMarkets.slice(0, 4).map((rm) => (
                    <div
                      key={rm.id}
                      onClick={() => onRelatedClick(rm)}
                      className="flex items-center justify-between gap-3 cursor-pointer p-2 -mx-2 transition-colors rounded-lg"
                      style={{ fontSize: Typography.Caption }}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {rm.image && (
                          <img
                            src={rm.image}
                            alt=""
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              objectFit: 'cover',
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <span
                          style={{
                            color: '#475569',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {rm.question}
                        </span>
                      </div>
                      <span
                        className="font-mono flex-shrink-0"
                        style={{ fontWeight: Typography.WeightBold, color: '#0f172a' }}
                      >
                        {rm.outcomes[0]?.probability.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailView;
